import { useGetDocumentImportHistory } from "@/queries/integrations/document-imports-history";
import { format } from "date-fns";
import { de } from "date-fns/locale";
import { ArrowDownToLine } from "lucide-react";
import { useTranslation } from "react-i18next";

import { Separator } from "@/components/ui/separator";
import { TypographyMuted, TypographySmall } from "@/components/ui/typography";
import { cn } from "@/lib/utils";

const KANZLEILAND_INTEGRATION_NAME = "kanzleiland";

const formatDate = (dateValue: string | number) => {
  try {
    // Handle timestamp as number (milliseconds) or ISO string
    const timestamp =
      typeof dateValue === "string" ? Number(dateValue) : dateValue;
    const date = Number.isNaN(timestamp)
      ? new Date(dateValue)
      : new Date(timestamp);

    if (Number.isNaN(date.getTime())) {
      return String(dateValue);
    }
    return format(date, "dd.MM.yyyy", { locale: de });
  } catch {
    return String(dateValue);
  }
};

export const DocumentImportHistory = () => {
  const { t } = useTranslation();
  const { history, loading } = useGetDocumentImportHistory({
    integrationName: KANZLEILAND_INTEGRATION_NAME,
  });

  if (loading) {
    return null;
  }

  if (history.length === 0) {
    return null;
  }

  return (
    <div className="flex flex-col gap-2 w-full mt-4">
      <Separator className="mb-2" />
      <TypographySmall>
        {t("integrations.kanzleiland.importHistory")}
      </TypographySmall>
      <div className="py-2 px-4 w-full">
        {history.map((item, index) => (
          <div key={item.date} className="flex flex-row gap-2 w-full">
            <div className="flex flex-col items-center">
              <div className="w-4 h-4 bg-muted rounded-full flex items-center justify-center">
                <ArrowDownToLine size={11} />
              </div>
              {index !== history.length - 1 && (
                <div className="flex flex-1 justify-center w-4 my-1">
                  <Separator orientation="vertical" />
                </div>
              )}
            </div>
            <div className="flex-1 min-h-[25px] mb-2">
              <div className="grid w-full items-center gap-4">
                <div className="flex flex-col space-y-1">
                  <div
                    className={cn(
                      index > 0 && index === history.length - 1 && "pb-2",
                    )}
                  >
                    <TypographyMuted className="text-xs">
                      <span className="font-semibold text-primary">
                        {item.count}{" "}
                        {t("integrations.kanzleiland.documentsImported")}
                      </span>
                      <span> · </span>
                      <span>{formatDate(item.date)}</span>
                    </TypographyMuted>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};
